import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { uniq, shuffle } from 'underscore';
import Slider from 'react-slick';
import useData from '../hooks/useData';
import useUI from '../hooks/useUI';
import useApp from '../hooks/useApp';
import IconClose from '../icons/IconClose';
import IconHeart from '../icons/IconHeart';
import IconHeartFilled from '../icons/IconHeartFilled';
import IconMarkerSearch from '../icons/IconMarkerSearch';
import IconInstagram from '../icons/IconInstagram';
import IconFacebook from '../icons/IconFacebook';
import IconLINE from '../icons/IconLINE';
import useRecord from '../hooks/useRecord';
import textareaToHTML from '../tools/textareaToHTML';
import useConfig from '../hooks/useConfig';
import useRecommend from '../hooks/useRecommend';

const Prev = (props) => {
	return <div className='nav prev' onClick={props.onClick} />;
};

const Next = (props) => {
	return <div className='nav next' onClick={props.onClick} />;
};

const ExhibitorInfo = () => {
	const { exhibitorInfo, toggleFavourite } = useData();
	const { ExhibitorInfo, toggleExhibitorInfo } = useUI();
	const { isCHT, selectedExhibitor, setSelectedExhibitor, selectedProduct, setSelectedProduct, setSelectedBooth, setActiveFloor, setAppState } = useApp();
	const [product, setProduct] = useState(null);
	const [sliderCurrent, setSliderCurrent] = useState(null);
	const { makeRecord } = useRecord();
	const config = useConfig();
	const { fetchRecommend, relatedExhibitorsFromAPI } = useRecommend();

	const onSliderLoad = useCallback((ref) => {
		setSliderCurrent(ref);
	}, []);

	useEffect(() => {
		if (selectedProduct) {
			setProduct(selectedProduct);
		}
	}, [selectedProduct]);

	const exhibitor = useMemo(() => {
		return exhibitorInfo.find((exhibitor) => exhibitor['攤位號碼'] === selectedExhibitor);
	}, [exhibitorInfo, selectedExhibitor]);

	const closePopup = useCallback(() => {
		toggleExhibitorInfo();
		setTimeout(() => {
			setSelectedExhibitor(null);
			setSelectedProduct(null);
			setProduct(null);
		}, 500);
	}, [toggleExhibitorInfo, setSelectedExhibitor, setSelectedProduct, setProduct]);

	const toggleFavouriteExhibitor = useCallback(() => {
		toggleFavourite(selectedExhibitor);
	}, [selectedExhibitor, toggleFavourite]);

	const categories = useMemo(() => {
		const allCategoriesCHT = [];
		const allCategoriesENG = [];

		exhibitor?.products.forEach((product) => {
			if (product['產品小分類']) {
				allCategoriesCHT.push(product['產品小分類']);
				allCategoriesENG.push(product['產品小分類-英文']);
				return;
			}

			if (product['產品中分類']) {
				allCategoriesCHT.push(product['產品中分類']);
				allCategoriesENG.push(product['產品中分類-英文']);
				return;
			}

			if (product['產品大分類']) {
				allCategoriesCHT.push(product['產品大分類']);
				allCategoriesENG.push(product['產品大分類-英文']);
				return;
			}
		});

		const uniqueCategoriesCHT = uniq(allCategoriesCHT);
		const uniqueCategoriesENG = uniq(allCategoriesENG);

		return {
			uniqueCategoriesCHT,
			uniqueCategoriesENG,
		};
	}, [exhibitor]);

	const uniqueCategories = useMemo(() => {
		if (isCHT) return categories.uniqueCategoriesCHT;
		return categories.uniqueCategoriesENG;
	}, [isCHT, categories]);

	const categoryLevel = useMemo(() => {
		const firstProduct = exhibitor?.products[0];
		if (!firstProduct) return null;
		if (firstProduct['產品小分類']) return '產品小分類';
		if (firstProduct['產品中分類']) return '產品中分類';
		if (firstProduct['產品大分類']) return '產品大分類';
		return null;
	}, [exhibitor]);

	const relatedExhibitors = useMemo(() => {
		const relatedExhibitors = [];
		const otherExhibitors = exhibitorInfo.filter((exhibitor) => exhibitor['攤位號碼'] !== selectedExhibitor);

		otherExhibitors.forEach((exhibitor) => {
			const newExhibitor = { ...exhibitor };

			if (categoryLevel === '產品小分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					if (isCHT) return uniqueCategories.includes(product['產品小分類']);
					return uniqueCategories.includes(product['產品小分類-英文']);
				});
			}

			if (categoryLevel === '產品中分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					if (isCHT) return uniqueCategories.includes(product['產品中分類']);
					return uniqueCategories.includes(product['產品中分類-英文']);
				});
			}

			if (categoryLevel === '產品大分類') {
				newExhibitor.products = exhibitor.products.filter((product) => {
					if (isCHT) return uniqueCategories.includes(product['產品大分類']);
					return uniqueCategories.includes(product['產品大分類-英文']);
				});
			}

			if (newExhibitor.products.length > 0) {
				relatedExhibitors.push(newExhibitor);
			}
		});
		return shuffle(relatedExhibitors);
	}, [exhibitorInfo, selectedExhibitor, categoryLevel, uniqueCategories, isCHT]);

	useEffect(() => {
		if (exhibitor && exhibitor['攤位號碼']) {
			fetchRecommend(exhibitor['攤位號碼']);
		}
	}, [exhibitor, fetchRecommend]);

	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const recordProductClicked = useCallback((product, booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth || selectedExhibitor,
			Product: product['產品名稱-中文'],
		});
	}, [makeRecord, selectedExhibitor]);

	const showProduct = useCallback((product) => {
		recordProductClicked(product);
		setProduct(product);
	}, [recordProductClicked]);

	const chooseExhibitor = useCallback((exhibitor) => {
		setActiveFloor(exhibitor.floor);
		setSelectedBooth(exhibitor['攤位號碼']);
		setSelectedExhibitor(exhibitor['攤位號碼']);
		setProduct(null);
	}, [setActiveFloor, setSelectedBooth, setSelectedExhibitor]);

	const chooseProduct = useCallback((relatedExhibitor, index) => {
		setSelectedExhibitor(relatedExhibitor['攤位號碼']);
		setActiveFloor(relatedExhibitor.floor);
		setSelectedBooth(relatedExhibitor['攤位號碼']);
		const product = relatedExhibitor.products[index];
		setProduct(product);
		recordProductClicked(product, relatedExhibitor['攤位號碼']);
	}, [recordProductClicked, setActiveFloor, setSelectedBooth, setSelectedExhibitor]);

	useEffect(() => {
		if (exhibitor && product) {
			const exhibitorProductIndex = exhibitor.products.findIndex((p) => p['產品名稱-中文'] === product['產品名稱-中文']);
			sliderCurrent.slickGoTo(exhibitorProductIndex);
		}
	}, [exhibitor, product, sliderCurrent]);

	const close = useCallback((event) => {
		if (event.target === event.currentTarget) {
			toggleExhibitorInfo();
			setTimeout(() => {
				setSelectedExhibitor(null);
				setSelectedProduct(null);
				setProduct(null);
			}, 500);
		}
	}, [toggleExhibitorInfo, setSelectedExhibitor, setSelectedProduct, setProduct]);

	const slidesToShow = useMemo(() => {
		if (window.innerWidth <= 768) return exhibitor?.products.length > 1 ? 2 : 1;
		return exhibitor?.products.length > 2 ? 3 : 1;
	}, [exhibitor]);

	const [scrollerCurrent, setScrollerCurrent] = useState(null);
	const onScrollerLoad = useCallback((ref) => {
		setScrollerCurrent(ref);
	}, []);

	const navigateToBooth = useCallback(() => {
		makeRecord({
			Action: 'N',
			Booth: selectedExhibitor,
		});

		setAppState((appState) => {
			return {
				...appState,
				selectedBooth: selectedExhibitor,
				navigation: true,
			}
		});

		toggleExhibitorInfo();
	}, [makeRecord, selectedExhibitor, setAppState, toggleExhibitorInfo]);

	const city = useMemo(() => {
		if (exhibitor) {
			return isCHT ? exhibitor['所在城市-中文'] : exhibitor['所在城市-英文'];
		}
		return null;
	}, [exhibitor, isCHT]);

	const logoUrl = useMemo(() => {
		if (exhibitor) {
			if (exhibitor.LOGO.includes('http')) {
				return exhibitor.LOGO;
			}
			return `./exhibitor-logos/${exhibitor.LOGO}`;
		}
		return null;
	}, [exhibitor]);

	const [relatedExhibitorsToDisplay, setRelatedExhibitorsToDisplay] = useState(3);

	const recommendedExhibitors = useMemo(() => {
		let recommendedExhibitors = [];

		if (config.recommendFromAPI) recommendedExhibitors = relatedExhibitorsFromAPI;
		if (!config.recommendFromAPI) recommendedExhibitors = relatedExhibitors;

		if (recommendedExhibitors.length > relatedExhibitorsToDisplay) {
			return recommendedExhibitors.slice(0, relatedExhibitorsToDisplay);
		}

		return recommendedExhibitors;
	}, [relatedExhibitors, relatedExhibitorsToDisplay, relatedExhibitorsFromAPI, config.recommendFromAPI]);

	const showMoreButton = useMemo(() => {
		if (relatedExhibitors.length > relatedExhibitorsToDisplay) {
			return true;
		}
		return false;
	}, [relatedExhibitors, relatedExhibitorsToDisplay]);

	useEffect(() => {
		if (selectedExhibitor) {
			setRelatedExhibitorsToDisplay(3);
		}
	}, [selectedExhibitor]);

	const showRightContent = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const sug = params.get('sug');
		const configSUG = config.showRightContent;

		if (!configSUG) return false;
		if (configSUG && sug === 'false') return false;
		return true;
	}, [config.showRightContent]);

	const productImageUrl = useMemo(() => {
		if (product) {
			if (product['產品圖'].includes('http')) {
				return product['產品圖'];
			}
			return `./product-photos/${product['產品圖']}`;
		}
		return null;
	}, [product]);

	useEffect(() => {
		if (exhibitor && !product) {
			if (exhibitor.products.length) {
				setProduct(exhibitor.products[0]);
			}
		}
	}, [exhibitor, product, setProduct]);

	if (!exhibitor) return null;

	return (
		<div id='ExhibitorInfo' className={ExhibitorInfo ? 'active' : ''} onClick={close}>
			<div className={`center-content ${showRightContent ? '' : 'narrow'}`}>
				<IconClose onClick={closePopup} />

				<div className='logo-wrap'>
					<img src={logoUrl} alt='LOGO' />
				</div>

				<div className='title'>{isCHT ? exhibitor['廠商名稱-中文'] : exhibitor['廠商名稱-英文']}</div>

				<div className='scroller' ref={onScrollerLoad}>

					<div className='flex-wrap-fixed'>
						<div className='left-content'>
							<div className='flex-between'>
								<div className='controls'>
									<div className='flex-control' onClick={toggleFavouriteExhibitor}>
										<div className='icon' >
											{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}
										</div>
										<div className='text'>{isCHT ? '攤位' : 'Booth'}：{selectedExhibitor}</div>
									</div>

									<div className='flex-control' onClick={navigateToBooth}>
										<div className='icon marker' >
											<IconMarkerSearch />
										</div>
										<div className='text'>{isCHT ? '導航路徑' : 'Navigate'}</div>
									</div>
								</div>

								<div className='links'>
									{exhibitor['社群連結-IG'] ? (
										<a href={exhibitor['社群連結-IG']} target='_blank' rel='noreferrer noopener'>
											<IconInstagram />
										</a>
									) : null}

									{exhibitor['社群連結-FB'] ? (
										<a href={exhibitor['社群連結-FB']} target='_blank' rel='noreferrer noopener'>
											<IconFacebook />
										</a>
									) : null}

									{exhibitor['社群連結-LINE'] ? (
										<a href={exhibitor['社群連結-LINE']} target='_blank' rel='noreferrer noopener'>
											<IconLINE />
										</a>
									) : null}
								</div>
							</div>

							<div className='tags'>
								{uniqueCategories.map((category, index) => (
									<div className='tag' key={`uc${index}`}>#{category}</div>
								))}
							</div>

							<div className='website'>
								<a href={exhibitor['官網連結']} target='_blank' rel='noreferrer noopener'>{exhibitor['官網連結']}</a>
							</div>

							<div className='location'>
								{city}{city && `, `}{isCHT ? exhibitor['國別-中文'] : exhibitor['國別-英文']}
							</div>

							<div className='description' dangerouslySetInnerHTML={{ __html: isCHT ? textareaToHTML(exhibitor['展商基本介紹-中文']) : textareaToHTML(exhibitor['展商基本介紹-英文']) }} />

							<div className='products'>
								<Slider
									infinite={true}
									slidesToShow={slidesToShow}
									dots={false}
									arrows={true}
									swipeToSlide={true}
									prevArrow={<Prev />}
									nextArrow={<Next />}
									ref={onSliderLoad}
									responsive={[
										{
											breakpoint: 768,
											settings: {
												slidesToShow: slidesToShow,
											}
										},
										{
											breakpoint: 480,
											settings: {
												slidesToShow: 1,
											}
										}
									]}
								>
									{exhibitor.products.map((product, index) => {
										const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;

										return (
											<div className='product-wrap' key={`exhibitor-product-${index}`} onClick={() => { showProduct(product) }}>
												<div className='product'>
													<div className='image-wrap' style={{ backgroundImage: `url(${imageUrl})` }}>
													</div>
													<div className='product-name'>{isCHT ? product['產品名稱-中文'] : product['產品名稱-英文']}</div>
												</div>
											</div>
										)
									})}
								</Slider>
							</div>

							{product ? (
								<div className='product-detail'>
									<div className='product-image' style={{ backgroundImage: `url(${productImageUrl})` }}></div>
									<div className='product-name'>{isCHT ? product['產品名稱-中文'] : product['產品名稱-英文']}</div>
									<div className='product-details' dangerouslySetInnerHTML={{ __html: isCHT ? textareaToHTML(product['產品說明-中文']) : textareaToHTML(product['產品說明-英文']) }}></div>
								</div>
							) : null}

							<div className='mobile-products'>
								{exhibitor.products.map((prod, index) => {
									const imageUrl = prod['產品圖'].includes('http') ? prod['產品圖'] : `./product-photos/${prod['產品圖']}`;

									return (
										<div className='product-detail-mobile' key={`prodetail${index}`}>
											<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
											<div className='product-name'>{isCHT ? prod['產品名稱-中文'] : prod['產品名稱-英文']}</div>
											<div className='product-details' dangerouslySetInnerHTML={{ __html: isCHT ? textareaToHTML(prod['產品說明-中文']) : textareaToHTML(prod['產品說明-英文']) }}></div>
										</div>
									)
								})}
							</div>
						</div>

						{showRightContent ? (
							<div className='right-content'>
								<div className='content-title'>{isCHT ? '推薦廠商' : 'Recommended'}</div>

								<div className='related-exhibitors'>
									{recommendedExhibitors.map((exhibitor, index) => {
										return (
											<div className='exhibitor' key={`related-exhibitor-${index}`}>
												<div className='exhibitor-name' onClick={() => { chooseExhibitor(exhibitor); recordExhibitorClicked(exhibitor['攤位號碼']); }}>{exhibitor[`廠商名稱-${isCHT ? '中文' : '英文'}`]}</div>
												<div className='related-products'>
													{exhibitor.products.map((product, i) => {
														if (i > 2) return null;
														const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
														return (
															<div className='product-wrap' key={`related-product-${index}${i}`} onClick={() => {
																chooseProduct(exhibitor, i, product);
																scrollerCurrent.scrollTo(0, 0);
															}}>
																<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
																<div className='product-name'>{product[`產品名稱-${isCHT ? '中文' : '英文'}`]}</div>
															</div>
														)
													})}
												</div>
											</div>
										)
									})}
								</div>

								{showMoreButton ? (
									<div className='flex-button'>
										<div className='show-more' onClick={() => { setRelatedExhibitorsToDisplay(relatedExhibitorsToDisplay + 3) }}>
											{isCHT ? '顯示更多' : 'Show More'}
										</div>
									</div>
								) : null}
							</div>
						) : null}

					</div>

				</div>
			</div>
		</div>
	)
}

export default ExhibitorInfo;